import { useState } from 'react'
import { useSelector } from 'react-redux'
import classNames from 'classnames'
import moment from 'moment'
import { useHistory } from 'react-router'

import featureToggles from 'shared/featureToggles'
import { CARD, getPaymentMethod } from 'shared/transactions'
import CancellationConfirmationModal from 'shared/components/CancellationConfirmationModal'

import Button from '@components/Button'

import DateTime from './DateTime'

import {
    cancelReservation,
    confirmCancelReservation,
    resetReservationFailedError,
    getReservationCancelAmendPolicy,
} from 'dashboard-transactions/actions'
import { clearVehicleUpdatedState } from 'manage-guest-user-booking/actions'

import { reservationCancelAmendPolicyPendingSelector, cancelReservationPendingSelector } from 'grs/selectors'
import { cancellationSelector, reservationCreditCardInfoSelector } from 'dashboard-transactions/selectors'
import { languageSelector } from 'i18n/selectors'

import { getReservationsByParamErrorSelector, vehicleUpdatedSelector } from 'manage-guest-user-booking/selectors'

import EditVehicleModal from './EditVehicleModal'

import styles from './styles.module.scss'

const BookingDetails = ({
    reservation: {
        reservationId,
        location,
        status,
        allowEditVehicle,
        vehicles,
        canAmend,
        canCancel,
        amountPaid,
        stayFrom,
        stayTo,
    },
    reservation,
    getReservationCancelAmendPolicy,
    reservationCancelAmendPolicyPending,
    cancelReservation,
    cancelBookingPending,
    confirmCancelReservation,
    cancellationInfo,
    vehicleUpdated,
    clearVehicleUpdatedState,
    resetReservationFailedError,
    bookingCancelledCallback,
    creditCardInfo,
}) => {
    const history = useHistory()
    const language = useSelector(languageSelector)
    const [isCancelBookingModalOpen, setIsCancelBookingModalOpen] = useState(false)
    const [isEditVehicleModalOpened, setIsEditVehicleModalOpened] = useState(false)
    const { waitingForCancelConfirmation, error: cancellationError } = cancellationInfo

    const paymentMethod = reservation && getPaymentMethod(reservation)

    const onEditStayHandler = async () => {
        await getReservationCancelAmendPolicy(reservationId)

        if (canAmend) {
            history.push(`/${language}/booking/${reservation.locationId}/edit/${encodeURIComponent(reservationId)}`)
        }
    }

    const onCancelBookingBtnHandler = e => {
        e.stopPropagation()
        getReservationCancelAmendPolicy(reservationId)
        setIsCancelBookingModalOpen(true)
    }

    const onCloseCancelBookingModalHandler = () => {
        setIsCancelBookingModalOpen(false)
    }

    const onCancelReservationConfirmHandler = async () => {
        resetReservationFailedError()
        cancelReservation(reservationId)
        confirmCancelReservation(reservationId)
    }

    React.useEffect(() => {
        if (vehicleUpdated) {
            setIsEditVehicleModalOpened(false)
            clearVehicleUpdatedState()
        }
    }, [vehicleUpdated])

    React.useEffect(() => {
        const cancelSuccess = isCancelBookingModalOpen && !waitingForCancelConfirmation && !cancellationError

        if (cancelSuccess && bookingCancelledCallback) {
            setIsCancelBookingModalOpen(false)
            bookingCancelledCallback && bookingCancelledCallback()
        }
    }, [waitingForCancelConfirmation])

    return (
        <div
            className={classNames(
                'd-sm-flex align-items-md-stretch justify-content-between no-gutters',
                styles.bookingDetails
            )}>
            <div className={styles.detailsContainer}>
                <div className={classNames(styles.detail, styles.location)}>{location}</div>
                {canCancel && (
                    <Button
                        btnStyle="outline"
                        size="small"
                        className={styles.bookingDetailsButton}
                        onClick={onCancelBookingBtnHandler}>
                        <T>manage.my.booking.cancel.booking.btn</T>
                    </Button>
                )}
            </div>
            <div className={classNames(styles.dateDetails, styles.detailsContainer)}>
                <DateTime fromDate={moment(stayFrom)} toDate={moment(stayTo)} />
                {canAmend && featureToggles.enableEditBooking && (
                    <Button
                        btnStyle="outline"
                        size="small"
                        className={styles.bookingDetailsButton}
                        onClick={onEditStayHandler}>
                        <T>manage.my.booking.edit.stay.btn</T>
                    </Button>
                )}
            </div>
            <div className={styles.detailsContainer}>
                <div className={classNames(styles.detail, styles.status)}>
                    <T>{status}</T>
                </div>
            </div>
            <div className={styles.detailsContainer}>
                <div className={classNames(styles.detail, styles.vehicle)}>
                    {vehicles?.map(vehicle => vehicle.tag).join(', ')}
                </div>
                {allowEditVehicle && (
                    <Button
                        btnStyle="outline"
                        size="small"
                        className={classNames(styles.bookingDetailsButton)}
                        onClick={() => setIsEditVehicleModalOpened(true)}>
                        <T>manage.my.booking.edit.vehicle.btn</T>
                    </Button>
                )}
            </div>
            <div className={styles.detailsContainer}>
                <div className={classNames(styles.detail, styles.paymentMethod)}>
                    <T>{`dashboard.transaction.payment.${paymentMethod}`}</T>
                </div>
                {paymentMethod === CARD && <div className="payment-card-info">{creditCardInfo}</div>}
            </div>
            {isEditVehicleModalOpened && (
                <EditVehicleModal
                    vehicle={reservation.vehicles[0]}
                    reservationId={reservationId}
                    onClose={() => setIsEditVehicleModalOpened(false)}
                    onCancelClick={() => setIsEditVehicleModalOpened(false)}
                />
            )}
            {cancellationInfo && isCancelBookingModalOpen && (
                <CancellationConfirmationModal
                    guestBooking
                    initialAmountPaid={amountPaid}
                    reservationId={reservationId}
                    cancellationData={cancellationInfo}
                    confirm={onCancelReservationConfirmHandler}
                    cancelReservationPending={cancelBookingPending}
                    cancelAmendPolicyReservationPending={reservationCancelAmendPolicyPending}
                    closeModal={onCloseCancelBookingModalHandler}
                    cancellationError={cancellationError}
                />
            )}
        </div>
    )
}

export default compose(
    connect(
        createStructuredSelector({
            getReservationsError: getReservationsByParamErrorSelector,
            reservationCancelAmendPolicyPending: reservationCancelAmendPolicyPendingSelector,
            cancelBookingPending: cancelReservationPendingSelector,
            cancellationInfo: cancellationSelector,
            vehicleUpdated: vehicleUpdatedSelector,
            creditCardInfo: reservationCreditCardInfoSelector,
        }),
        {
            getReservationCancelAmendPolicy,
            cancelReservation,
            confirmCancelReservation,
            clearVehicleUpdatedState,
            resetReservationFailedError,
        }
    )
)(BookingDetails)
